import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight, Facebook, Instagram, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/common/Primitives";
import { LogoHorizontal, LogoStacked } from "@/components/common/Logo";
import { api, errorCode } from "@/lib/api";
import { errorText, useLang } from "@/lib/i18n";
import { HOTEL } from "@/lib/hotel";
import { NAV, LangSwitch } from "./Header";

/* ------------------------------------------------------------------ newsletter */

export const Newsletter = ({ tone = "light" }) => {
  const { lang, tx } = useLang();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const dark = tone === "dark";
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      const { data } = await api.post("/newsletter", { email, name: "", lang });
      setState("done");
      toast.success(data.already ? tx("Du er alt på lista.", "You're already on the list.") : tx("Takk! Du er på lista.", "Thank you! You're on the list."));
    } catch (err) {
      setState("idle");
      toast.error(errorText(errorCode(err), lang));
    }
  };
  return (
    <div data-testid="newsletter-section">
      <p className={`text-sm font-semibold ${dark ? "text-white/60" : "text-muted-foreground"}`}>{tx("Nyheitsbrev", "Newsletter")}</p>
      <p className={`mt-3 text-[1.25rem] font-semibold leading-snug tracking-[-0.02em] ${dark ? "text-white" : "text-foreground"}`}>{tx("Konsertar, julebord, bassengopning og sesongmenyar.", "Concerts, Christmas dinners, pool opening and seasonal menus.")}</p>
      <p className={`mt-2 text-[0.9375rem] leading-relaxed ${dark ? "text-white/60" : "text-muted-foreground"}`}>{tx("Ei melding av og til. Meld deg av når du vil.", "A message now and then. Unsubscribe whenever you like.")}</p>
      {state === "done" ? (
        <div className={`mt-5 flex items-center gap-3 rounded-full px-5 py-3.5 text-[0.9375rem] ${dark ? "bg-white/10 text-white" : "bg-accent text-accent-foreground"}`} data-testid="newsletter-success">
          <Check className="h-5 w-5" /> {tx("Du er på lista. Me høyrest!", "You're on the list. Speak soon!")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-3">
          <div className={`flex items-center gap-1 rounded-full p-1 transition-[border-color,box-shadow,background-color] focus-within:ring-2 ${dark ? "border border-white/15 bg-white/[0.06] focus-within:border-white/30 focus-within:ring-white/15" : "border border-input bg-card focus-within:border-ring focus-within:ring-ring/30"}`}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={tx("E-postadressa di", "Your email address")}
              className={`h-11 min-w-0 flex-1 bg-transparent pl-4 text-[0.9375rem] outline-none ${dark ? "text-white placeholder:text-white/40" : "text-foreground placeholder:text-muted-foreground/70"}`}
              data-testid="footer-newsletter-email"
              aria-label={tx("E-postadresse", "Email address")}
              autoComplete="email"
            />
            <Button type="submit" variant={dark ? "inverse" : "default"} className="h-11 shrink-0 px-5" disabled={state === "loading"} data-testid="footer-newsletter-submit">
              {state === "loading" ? tx("Sender …", "Sending …") : tx("Meld meg på", "Sign up")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ footer */

const Col = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <p className="text-sm font-semibold text-white/55">{title}</p>
    <ul className="mt-4 space-y-2.5 text-[0.9375rem] text-white/80">{children}</ul>
  </div>
);

const FLink = ({ to, children, testid }) => (
  <li>
    <Link to={to} className="group/f inline-flex items-center gap-1 transition-colors hover:text-white" data-testid={testid}>
      <span className="relative">
        {children}
        <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-white/70 transition-transform duration-200 ease-premium group-hover/f:scale-x-100" aria-hidden />
      </span>
    </Link>
  </li>
);

/** Live local time in Skånevik – a small, honest sign of life */
const LocalClock = () => {
  const { lang, tx } = useLang();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const time = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nb-NO", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Oslo" }).format(now);
  return (
    <span className="inline-flex items-center gap-2 tabular" data-testid="footer-local-time">
      <Clock className="h-3.5 w-3.5" aria-hidden /> {tx("Klokka i Skånevik", "Local time in Skånevik")} {time.replace(":", lang === "en" ? ":" : ".")}
    </span>
  );
};

export const Footer = ({ compact = false }) => {
  const { tx } = useLang();
  return (
    <footer className="relative overflow-hidden bg-[hsl(214,32%,9%)] text-white" data-testid="site-footer">
      {!compact && (
        <Container className="relative pt-16 lg:pt-24">
          {/* Statement + newsletter */}
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <LogoStacked className="h-14 text-white sm:h-16" />
              <h2 className="mt-9 max-w-[14ch] text-[2.25rem] font-semibold leading-[0.98] tracking-[-0.035em] text-white sm:text-[2.75rem] lg:text-[3.5rem]" data-testid="footer-statement">
                {tx("Alle vegar fører til Skånevik.", "All roads lead to Skånevik.")}
              </h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-white/65">
                {tx("Bestill direkte hjå oss – beste pris, frukost inkludert, og du betaler når du kjem.", "Book directly with us – best rate, breakfast included, and you pay when you arrive.")}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg" variant="inverse" data-testid="footer-book-button">
                  <Link to="/bestill">{tx("Bestill rom", "Book a room")} <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="ghost-light" className="border border-white/20 tabular" data-testid="footer-phone">
                  <a href={HOTEL.tel}><Phone className="h-4 w-4" /> {HOTEL.phone}</a>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Newsletter tone="dark" />
            </div>
          </div>

          {/* Columns */}
          <div className="mt-14 grid grid-cols-2 gap-x-8 gap-y-10 border-t border-white/10 pt-12 sm:grid-cols-4 lg:mt-20 lg:pt-14">
            <Col title={tx("Utforsk", "Explore")} testid="footer-col-explore">
              {NAV.map((n) => <FLink key={n.to} to={n.to} testid={`footer-nav-${n.id}`}>{tx(n.nn, n.en)}</FLink>)}
            </Col>
            <Col title={tx("Bestilling", "Booking")} testid="footer-col-booking">
              <FLink to="/bestill" testid="footer-booking-cta">{tx("Bestill rom", "Book a room")}</FLink>
              <FLink to="/mi-bestilling" testid="footer-my-booking">{tx("Mi bestilling", "My booking")}</FLink>
              <FLink to="/arrangement#forespurnad">{tx("Selskap eller møte", "Events or meetings")}</FLink>
              <FLink to="/mat#bord">{tx("Bestill bord", "Book a table")}</FLink>
              <FLink to="/praktisk#vilkar">{tx("Bestillingsvilkår", "Booking terms")}</FLink>
            </Col>
            <Col title={tx("Godt å vite", "Good to know")} testid="footer-col-info">
              <li className="tabular">{tx("Innsjekk frå kl. 15.00", "Check-in from 15:00")}</li>
              <li className="tabular">{tx("Utsjekk før kl. 12.00", "Check-out before 12:00")}</li>
              <li className="tabular">{tx("Frukost kl. 07.30–10.00", "Breakfast 07:30–10:00")}</li>
              <li>{tx("Gratis parkering og elbillading", "Free parking and EV charging")}</li>
              <li>{tx("Betaling på hotellet", "Payment at the hotel")}</li>
            </Col>
            <Col title={tx("Kontakt", "Contact")} testid="footer-col-contact">
              <li className="flex items-start gap-2 text-white/85"><MapPin className="mt-1 h-4 w-4 shrink-0 text-white/50" /><span>{HOTEL.street}<br />{HOTEL.postal}</span></li>
              <li><a href={HOTEL.tel} className="inline-flex items-center gap-2 transition-colors hover:text-white tabular" data-testid="footer-phone-link"><Phone className="h-4 w-4 text-white/50" /> {HOTEL.phone}</a></li>
              <li><a href={`mailto:${HOTEL.email}`} className="inline-flex items-center gap-2 break-all transition-colors hover:text-white" data-testid="footer-email"><Mail className="h-4 w-4 shrink-0 text-white/50" /> {HOTEL.email}</a></li>
              <li>
                <a href={HOTEL.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 transition-colors hover:text-white" data-testid="footer-directions">
                  {tx("Vegbeskriving", "Directions")} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
              <li className="flex items-center gap-2 pt-1">
                <a href={HOTEL.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/10" data-testid="footer-facebook"><Facebook className="h-4 w-4" /></a>
                <a href={HOTEL.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/10" data-testid="footer-instagram"><Instagram className="h-4 w-4" /></a>
              </li>
            </Col>
          </div>
        </Container>
      )}

      {/* Legal bar */}
      <Container className={`relative ${compact ? "py-6" : "mt-12 border-t border-white/10 pt-6 lg:mt-14"} pb-6`}>
        <div className="flex flex-col gap-5 text-sm text-white/55 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-5">
            <p className="tabular">© {new Date().getFullYear()} {HOTEL.name} · {tx("Mellom Preikestolen og Trolltunga", "Between Preikestolen and Trolltunga")}</p>
            <LocalClock />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Personvern", "Privacy")}</Link>
            <Link to="/praktisk#vilkar" className="transition-colors hover:text-white">{tx("Vilkår", "Terms")}</Link>
            <Link to="/admin/login" className="transition-colors hover:text-white" data-testid="footer-admin-link">{tx("For tilsette", "Staff login")}</Link>
            <LangSwitch testid="footer-language-toggle" light />
          </div>
        </div>
      </Container>

      {/* Oversized wordmark – the quiet signature at the very end of every page */}
      {!compact && (
        <div className="pointer-events-none relative mt-4 h-[9vw] max-h-[150px] min-h-[64px] overflow-hidden" aria-hidden data-testid="footer-wordmark">
          <LogoHorizontal title="" className="absolute left-1/2 top-2 h-[12vw] max-h-[200px] min-h-[84px] -translate-x-1/2 text-white/[0.07]" />
        </div>
      )}
      {/* room for the sticky mobile booking bar */}
      <div className="h-20 lg:hidden" aria-hidden />
    </footer>
  );
};
